// The What's new pigeon (README "What's new"): when there are updates this browser hasn't read (site/js/ui/news.js), a
// pigeon flies in with a letter and sits on the city's billboard until the visitor taps it (site/js/app.js), then flies
// off without the letter. While it sits, a badge over its head names the most notable kind of update it brings (NEW
// CITY, NEW, IMPROVED or FIXED). The letter's shine breathes and the head bobs for a while after it lands (SETTLE_MS),
// then it sits still with the letter lit: it can wait there for hours, beside someone's work. Its pose is a function of
// the page's clock (performance.now() ms); it is drawn into the page's overlay buffers with the lanterns, never into a
// scene (PNG, MP4 and the mini player stay clean). With the billboards off it sits on the player (DOCK, below). Poses
// and perches are tested in tests/news.test.mjs.
import { W, TAU, clamp, smooth, makeHalo, drawHalo, text, textWidth } from '../engine/core.js';
import { NEWS_TYPES } from './news.js';

export const ARRIVE_MS = 2600;      // from off the view to the billboard
export const TURN_MS = 200;         // once read: it turns round (the letter is gone), then leaves
export const LEAVE_MS = 1800;
export const FADE_MS = 400;         // reduced motion: it fades in and out where it sits
export const BADGE_MS = 250;        // the badge fades in once it has landed
const FLAP_MS = 100, BOB_EVERY = 3400, BOB_MS = 280;
const SHINE_MS = 2400, SHINE = 0.7;                  // one breath of the letter's shine, around this level
export const SETTLE_MS = 8 * SHINE_MS;               // from setting off: eight breaths (the shine is back at SHINE), then still

// Every frame faces right, 12×8, with its feet (the pose's x, y) at column FOOT_X of the bottom row. h head and back,
// b breast, w wing, d wing bars and tail, n and p the green and purple of the neck, e eye, c cere, k beak, f feet
export const ART = {
  perch: ['......hhh...', '......hheh..', '.....nhhhck.', '..wwwpnbb...', '.dwdwwbbbb..', 'dddwdwwbbb..', '....wwbbb...', '......f.f...'],
  bob:   ['.......hhh..', '.......hheh.', '.....nnhhhck', '..wwwpnbbb..', '.dwdwwbbbb..', 'dddwdwwbbb..', '....wwbbb...', '......f.f...'],
  up:    ['....dw......', '....www.....', '.....www.hh.', 'ddbbbbbnhhek', '.ddbbbbbb...', '............', '............', '............'],
  down:  ['............', '............', '.........hh.', 'ddbbbbbnhhek', '.ddbbwwbbb..', '....www.....', '....dw......', '............'],
  glide: ['............', '............', '..wwww...hh.', 'ddbwwwbnhhek', '.ddbbbbbb...', '............', '............', '............'],
};
export const FOOT_X = 7;
const SIZE = { w: 12, h: 8 };
// the letter's top-left corner in each frame (it hangs from the beak), in the frame's own columns and rows
const LETTER_AT = { perch: [10, 3], bob: [11, 3], up: [10, 4], down: [10, 4], glide: [10, 4] };
// m paper, s the flap's crease, r the wax seal
export const LETTER = ['mmmmm', 'msmsm', 'mmrmm', 'mmmmm'];
const COLOURS = {
  h: '#8790ab', b: '#b4bccf', w: '#767e98', d: '#3f4458', n: '#62b59a', p: '#a07cc4', e: '#15161f', c: '#e9e6df', k: '#3b3540',
  f: '#e0776c', m: '#f6f0de', s: '#c9bb98', r: '#d0473c',
};

/**
 * Where a pigeon sits on `scene`'s billboard: { x, y, side } (x, y = its feet, just above the frame's top edge; side +1
 * = the right-hand corner, so it comes in from the right and faces left; -1 the left-hand one). A scene can export
 * `perch: { dx, dy, side }` when its billboard's frame, lamps or roof need it elsewhere: dx from the corner inwards,
 * dy up from the default (the frame's top row two pixels above the face).
 */
export function perchOf(scene) {
  const bb = scene.billboard, o = scene.perch ?? {}, side = o.side ?? 1;
  const x = side > 0 ? bb.x + bb.w - 5 - (o.dx ?? 0) : bb.x + 4 + (o.dx ?? 0);
  return { x: clamp(Math.round(x), 8, W - 9), y: bb.y - 3 - (o.dy ?? 0), side };
}

const bezier = (a, c, b, u) => (1 - u) * (1 - u) * a + 2 * (1 - u) * u * c + u * u * b;

/**
 * The pose of pigeon `p` ({ perch, at, left, type }: perchOf, when it set off and when it was read, ms, left null until
 * then, and the kind of update its badge names, a key of NEWS_TYPES or null for none) at `now`: { x, y, frame, flip,
 * alpha, letter, glow, perched, type, badge } (x, y = feet, whole pixels; frame a key of ART; flip: facing left; letter:
 * it still has it; glow: the letter's shine; perched: sitting still, so it can be tapped; badge: how much of the badge
 * shows, 0 to 1), or null once it has gone. SETTLE_MS after it set off it sits still, the shine steady. `reduced`
 * (prefers-reduced-motion): it fades in and out on the billboard instead of flying, and is still from the start.
 */
export function pigeonPose(p, now, { reduced = false } = {}) {
  // read before it had landed (in another tab): it lands first
  const { x, y, side } = p.perch, since = now - p.at, gone = p.left == null ? -1 : now - Math.max(p.left, p.at + (reduced ? FADE_MS : ARRIVE_MS));
  const lively = !reduced && since < SETTLE_MS;
  const shine = lively ? SHINE + 0.3 * Math.sin(TAU * since / SHINE_MS) : SHINE, type = p.type ?? null;
  const sitting = frame => ({ x, y, frame, flip: side > 0, alpha: 1, letter: true, glow: shine, perched: true, type, badge: smooth(ARRIVE_MS, ARRIVE_MS + BADGE_MS, since) });
  if (reduced) {
    if (gone >= FADE_MS) return null;
    const alpha = smooth(0, FADE_MS, since) * (gone < 0 ? 1 : 1 - smooth(0, FADE_MS, gone));
    return { ...sitting('perch'), alpha, letter: gone < 0, perched: gone < 0 && since >= FADE_MS, badge: gone < 0 ? alpha : 0 };
  }
  if (gone >= 0) {                                    // read: it turns round without the letter, then flies off, out and up
    if (gone < TURN_MS) return { x, y, frame: 'perch', flip: side < 0, alpha: 1, letter: false, glow: 0, perched: false, type, badge: 0 };
    const u = Math.min(1, (gone - TURN_MS) / LEAVE_MS), e = u * u;
    if (u >= 1) return null;
    return {
      x: Math.round(bezier(x, x + side * 25, x + side * 110, e)), y: Math.round(bezier(y, y - 8, y - 95, e)),
      frame: Math.floor((gone - TURN_MS) / (FLAP_MS * 0.8)) % 2 ? 'down' : 'up', flip: side < 0,
      alpha: 1 - smooth(0.7, 1, u), letter: false, glow: 0, perched: false, type, badge: 0,
    };
  }
  if (since < 0) return null;
  if (since < ARRIVE_MS) {                            // in from high up on its side, levelling off onto the frame
    const u = since / ARRIVE_MS, e = 1 - (1 - u) ** 3;
    return {
      x: Math.round(bezier(x + side * 90, x + side * 12, x, e)), y: Math.round(bezier(y - 75, y - 5, y, e)),
      frame: u > 0.86 ? 'glide' : Math.floor(since / FLAP_MS) % 2 ? 'down' : 'up', flip: side > 0,
      alpha: smooth(0, 0.12, u), letter: true, glow: shine, perched: false, type, badge: 0,
    };
  }
  return sitting(lively && (since - ARRIVE_MS) % BOB_EVERY > BOB_EVERY - BOB_MS ? 'bob' : 'perch');   // a head bob now and then, until it settles
}

// The badge: the kind's word (NEWS_TYPES[type].badge) in FONT3, dark on a pill of its colour with the corners cut and
// 1 px of room round the letters, then a notch pointing down at the head, 1 px above it (the head's top row is the
// frame's, and centred on the feet' x whichever way the bird faces).
const BADGE_H = 9;                  // the pill's 7 rows and the notch's 2
/** The scene rect of the badge for `type` over a pigeon with its feet at `at` ({ x, y }), notch included, kept inside
 *  the scene. */
export function badgeRect(at, type) {
  const w = textWidth(NEWS_TYPES[type].badge) + 4;
  return { x: clamp(at.x - (w >> 1), 0, W - w), y: at.y - SIZE.h - BADGE_H, w, h: BADGE_H };
}

/** The scene rect of the sitting pigeon and its letter (either way round, head bob included), and of its badge when
 *  it has one (`type`), for display.spot. */
export function pigeonRect(perch, type = null) {
  const bird = { x: perch.x - FOOT_X - 1, y: perch.y - SIZE.h + 1, w: 17, h: SIZE.h };
  if (!type) return bird;
  const b = badgeRect(perch, type), x = Math.min(bird.x, b.x), right = Math.max(bird.x + bird.w, b.x + b.w);
  return { x, y: b.y, w: right - x, h: bird.y + bird.h - b.y };
}

// With no billboard to sit on (the visitor's Billboards setting, the admin page's switch) it sits on the player instead.
// site/js/app.js draws it into a canvas of its own in the dock: DOCK.w × DOCK.h scene pixels with the feet at DOCK.x,
// DOCK.y (room for the flight in from the right and out again, the letter's shine and the badge), shown at dockScale
// CSS px a pixel with the feet at dockPerch.
export const DOCK = { w: 160, h: 124, x: 30, y: 108, side: 1 };
/** CSS px per pixel of the pigeon on the player: as near the scene's (`k`) as a whole number gets, 2 to 4. */
export const dockScale = k => clamp(Math.round(k), 2, 4);
const BADGE_W = Math.max(...Object.values(NEWS_TYPES).map(t => textWidth(t.badge))) + 4;   // the widest badge

/**
 * Where on the page (CSS px) the pigeon's feet go with no billboard: { x, y }. On top of the player at its right-hand
 * end, where the widest badge still ends inside it; where the chips above the player reach that far (phones), on the
 * last chip; and on top of a card standing over that spot (the radio's vote, a nudge). `player`, `chip` (the last one
 * on show, or null) and `cards` (the ones on show) are page rects ({ left, right, top }), `k` dockScale, `width` the
 * window's: the badge stays inside it.
 */
export function dockPerch({ player, chip = null, cards = [], k, width }) {
  const half = Math.ceil(BADGE_W / 2) * k, most = width - half - 4;
  let x = Math.min(player.right - half - 8, most), y = player.top;
  if (chip && chip.right + 6 > x - half) {
    x = Math.min((chip.left + chip.right) / 2, most);
    y = chip.top;
  }
  for (const c of cards) {
    if (c.top >= y || c.left >= x + half || c.right <= x - half) continue;
    x = clamp(x, c.left + 20, c.right - 20);          // clear of its round corners
    y = c.top;
  }
  return { x: Math.round(x), y: Math.round(y) };
}

let art = null;                     // { right: { frame: canvas }, left: { … }, letter, halo, core }, drawn on first use
function paint(rows) {
  const c = document.createElement('canvas');
  c.width = rows[0].length; c.height = rows.length;
  const x = c.getContext('2d');
  rows.forEach((row, j) => [...row].forEach((ch, i) => {
    if (!COLOURS[ch]) return;
    x.fillStyle = COLOURS[ch];
    x.fillRect(i, j, 1, 1);
  }));
  return c;
}
const mirror = rows => rows.map(r => [...r].reverse().join(''));
function build() {
  const frames = rows => Object.fromEntries(Object.entries(ART).map(([k, v]) => [k, paint(rows(v))]));
  return {
    right: frames(r => r), left: frames(mirror), letter: paint(LETTER),
    halo: makeHalo(16, '#ffe7a8', 0.42), core: makeHalo(6, '#fff6d8', 0.55, 1.6),
  };
}

/** Where pose `pose` puts its frame's top-left corner ({ left, top }) and the letter's ({ x, y }, null without it). */
export function pigeonLayout(pose) {
  const left = pose.x - (pose.flip ? SIZE.w - 1 - FOOT_X : FOOT_X), top = pose.y - SIZE.h + 1;
  if (!pose.letter) return { left, top, letter: null };
  const [lx, ly] = LETTER_AT[pose.frame], w = LETTER[0].length;
  return { left, top, letter: { x: left + (pose.flip ? SIZE.w - lx - w : lx), y: top + ly } };
}

function drawBadge(ink, pose) {
  const { x, y, w } = badgeRect(pose, pose.type), { badge, colour } = NEWS_TYPES[pose.type];
  ink.globalAlpha = pose.badge;
  ink.fillStyle = colour;
  ink.fillRect(x + 1, y, w - 2, 7);
  ink.fillRect(x, y + 1, w, 5);
  ink.fillRect(pose.x - 1, y + 7, 3, 1);
  ink.fillRect(pose.x, y + 8, 1, 1);
  text(ink, badge, x + 2, y + 1, COLOURS.e);
}

/** Draws pose `pose` (pigeonPose): the letter's shine into `glow` (put on screen additively), bird, letter and badge
 *  into `ink`. `hot` (the tap spot is hovered or focused) makes the letter shine brighter. */
export function drawPigeon(glow, ink, pose, hot = false) {
  art ??= build();
  const { left, top, letter } = pigeonLayout(pose);
  ink.globalAlpha = pose.alpha;
  ink.drawImage(art[pose.flip ? 'left' : 'right'][pose.frame], left, top);
  if (letter) {
    const shine = pose.alpha * (hot ? 1.5 : pose.glow), cx = letter.x + (LETTER[0].length >> 1), cy = letter.y + 2;
    drawHalo(glow, art.halo, cx, cy, shine);
    drawHalo(glow, art.core, cx, cy, shine);
    ink.drawImage(art.letter, letter.x, letter.y);
  }
  if (pose.type && pose.badge > 0) drawBadge(ink, pose);
  ink.globalAlpha = 1;
}
